'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { Building2, Loader2, MapPin, X } from 'lucide-react'

import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { IconButton } from '../ui/button'

interface PlacePrediction {
  place_id: string
  description: string
  structured_formatting: {
    main_text: string
    secondary_text: string
  }
}

interface PlaceResult {
  place_id: string
  name: string
  formatted_address: string
  types?: string[]
}

interface GooglePlacesAutocompleteProps {
  onPlaceSelect: (placeId: string, placeName: string, address: string) => void
  placeholder?: string
  className?: string
  disabled?: boolean
  value?: string
  onClear?: () => void
}

/**
 * Fetch autocomplete predictions from API (server handles text search fallback)
 */
async function fetchAutocomplete(
  input: string,
): Promise<PlacePrediction[] | null> {
  if (!input.trim() || input.length < 3) {
    return null
  }

  try {
    const response = await fetch(
      `/api/google-places/autocomplete?input=${encodeURIComponent(input)}`,
    )
    const data = await response.json()
    if (data.predictions && data.predictions.length > 0) {
      return data.predictions
    }
  } catch (error) {
    console.error('Autocomplete fetch failed:', error)
  }

  return null
}

/**
 * Fetch place details from API
 */
async function fetchPlaceDetails(placeId: string): Promise<PlaceResult | null> {
  try {
    const response = await fetch(
      `/api/google-places/details?place_id=${placeId}`,
    )
    const data = await response.json()
    if (data.result) {
      return {
        place_id: placeId,
        name: data.result.name || '',
        formatted_address: data.result.formatted_address || '',
        types: data.result.types,
      }
    }
  } catch (error) {
    console.error('Place details fetch failed:', error)
  }

  return null
}

export function GooglePlacesAutocomplete({
  onPlaceSelect,
  placeholder = 'Search for your business...',
  className,
  disabled = false,
  value = '',
  onClear,
}: GooglePlacesAutocompleteProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const requestIdRef = useRef(0)

  const [inputValue, setInputValue] = useState(value)
  const [predictions, setPredictions] = useState<PlacePrediction[]>([])
  const [selectedPlace, setSelectedPlace] = useState<PlaceResult | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)

  // Sync with external value prop (controlled component)
  useEffect(() => {
    setInputValue(value)
  }, [value])

  /**
   * Handle clear - reset everything
   */
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()

    setInputValue('')
    setSelectedPlace(null)
    setPredictions([])
    setIsOpen(false)
    setIsLoading(false)
    setHasSearched(false)
    requestIdRef.current++

    if (inputRef.current) {
      inputRef.current.focus()
    }

    onClear?.()
    onPlaceSelect('', '', '')
  }

  /**
   * Handle input change with debounced search
   */
  const handleInputChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value
      setInputValue(newValue)

      if (selectedPlace) {
        setSelectedPlace(null)
      }

      if (newValue.length < 3) {
        setPredictions([])
        setIsOpen(false)
        setIsLoading(false)
        setHasSearched(false)
        return
      }

      const currentRequestId = ++requestIdRef.current
      setIsLoading(true)

      // Debounce
      await new Promise((resolve) => setTimeout(resolve, 400))

      if (currentRequestId !== requestIdRef.current) {
        return
      }

      const results = await fetchAutocomplete(newValue)

      if (currentRequestId !== requestIdRef.current) {
        return
      }

      setPredictions(results || [])
      setHasSearched(true)
      setIsOpen(true)
      setIsLoading(false)
    },
    [selectedPlace],
  )

  /**
   * Handle selection from dropdown
   */
  const handleSelectPrediction = useCallback(
    async (prediction: PlacePrediction) => {
      setIsLoading(true)
      setIsOpen(false)

      const details = await fetchPlaceDetails(prediction.place_id)

      if (details) {
        setSelectedPlace(details)
        setInputValue(details.name || details.formatted_address)
        onPlaceSelect(details.place_id, details.name, details.formatted_address)
      } else {
        const place: PlaceResult = {
          place_id: prediction.place_id,
          name: prediction.structured_formatting.main_text,
          formatted_address: prediction.description,
        }
        setSelectedPlace(place)
        setInputValue(prediction.structured_formatting.main_text)
        onPlaceSelect(
          prediction.place_id,
          prediction.structured_formatting.main_text,
          prediction.description,
        )
      }

      setIsLoading(false)
    },
    [onPlaceSelect],
  )

  /**
   * Handle keyboard
   */
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Escape') {
        setIsOpen(false)
      } else if (e.key === 'Enter' && isOpen && predictions.length > 0) {
        e.preventDefault()
        handleSelectPrediction(predictions[0])
      }
    },
    [isOpen, predictions, handleSelectPrediction],
  )

  // Left icon based on state
  const leftIcon = isLoading ? (
    <Loader2 className="w-4 h-4 text-gray-500 animate-spin" />
  ) : selectedPlace ? (
    <Building2 className="w-4 h-4 text-emerald-400" />
  ) : (
    <MapPin className="w-4 h-4 text-gray-500" />
  )

  return (
    <div className={cn('relative w-full', className)}>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <div>
            <Input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onFocus={() => {
                if (hasSearched && !selectedPlace) {
                  setIsOpen(true)
                }
              }}
              placeholder={placeholder}
              disabled={disabled}
              pre={leftIcon}
              suffix={
                inputValue && (
                  <IconButton
                    type="button"
                    onClick={handleClear}
                    className="h-8 w-8 border-0 text-white/80 hover:text-white hover:bg-white/10"
                  >
                    <X className="w-3.5 h-3.5 text-gray-500 hover:text-white" />
                  </IconButton>
                )
              }
              className={cn(
                'h-8 bg-transparent border-0 border-b border-white/10 rounded-none',
                'text-white placeholder-gray-600 text-xs',
                'focus-visible:border-gold/30 focus-visible:ring-0',
                selectedPlace && 'border-emerald-500/30 text-emerald-300',
              )}
              autoComplete="off"
            />
          </div>
        </PopoverTrigger>

        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] p-0 bg-[#1a1a1a] border-white/10"
          align="start"
          sideOffset={4}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <div className="max-h-60 overflow-y-auto">
            {predictions.length > 0 ? (
              predictions.map((prediction) => {
                return (
                  <button
                    key={prediction.place_id}
                    type="button"
                    onClick={() => handleSelectPrediction(prediction)}
                    className="w-full px-3 py-2.5 text-left hover:bg-white/10 transition-colors flex items-start gap-2 border-b border-white/5 last:border-b-0"
                  >
                    <MapPin className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate">
                        {prediction.structured_formatting.main_text}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {prediction.structured_formatting.secondary_text}
                      </p>
                    </div>
                  </button>
                )
              })
            ) : (
              <div className="px-3 py-4 text-center text-gray-500 text-sm max-w-sm">
                No results found for &quot;{inputValue}&quot;
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>

      {/* Selected place address */}
      {selectedPlace && (
        <div className="mt-1.5 text-xs text-gray-500 truncate">
          {selectedPlace.formatted_address}
        </div>
      )}
    </div>
  )
}

export default GooglePlacesAutocomplete
